import { Button, Card, Flex, Box, Text } from 'rebass';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc';
import { RxDragHandleDots2 } from "react-icons/rx";
import { FiEdit, FiTrash2 } from "react-icons/fi";

import { patchChapters, destroyChapter, postChapters } from '../../api';
import { useSpreadState } from '../../../app/hooks';
import ConfirmDelete from './confirm-delete';
import NewTag from './new-tag';
import arrayMove from 'array-move';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';
import { EditTagInput } from './styled';
import { width } from 'styled-system';

const SortableContainer = sortableContainer(({ children }) => <ul className="styled-ul" >{children}</ul>)

export const DragHandle = sortableHandle(() => (
  <Box
    className="handle"
    sx={{ zIndex: 9, position: 'relative', cursor: 'pointer' }}
    minWidth="unset"
    mr={2}
  >
    <RxDragHandleDots2 size={24} color="#64748B" />
  </Box>
));


function RenderChapter({ setState, tag, onClickTag, editing, editedTagName, onDone }) {
  const isEditingCurrent = editing === tag.name;

  return <Card px="20px" py={1} borderTop="1px solid" borderColor="border" mb={2} >
    <Flex alignItems="center" justifyContent="space-around" key={tag.name} >
      {!isEditingCurrent && <DragHandle />}
      {!isEditingCurrent && (
        <Button
          bg="transparent"
          width="100%"
          fontWeight="normal"
          onClick={onClickTag}
          sx={{
            border: 'none', textAlign: 'left',
            color: '#64748B',
            fontFamily: 'Poppins',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '165%', /* 26.4px */
            letterSpacing: '0.16px',
          }}
          p={0}
          mr={2}
        >
          {tag.name}
        </Button>
      )}
      {!editing && (
        <>
          <button
            onClick={() => {
              setState({
                editing: tag.name,
                editedTagName: tag.name,
              });
            }}
            type="button"
            style={{ marginLeft: 'auto', color: 'black' }}
          >
            <FiEdit size={24} color="#64748B" />
          </button>
          {/* <FavouriteButton collection={collection} id={tag.id} /> */}
        </>
      )}
      {editing === tag.name && (
        <Box width="100%">
          <EditTagInput
            type="text"
            value={editedTagName}
            onChange={e => setState({ editedTagName: e.target.value })}
            style={{
              width: '100%',
              border: '1px solid #9B59B6',
              background: '#F5EEF8',
            }}
            autoFocus
          />
          <Flex justifyContent="space-between">
            <Button
              p={0}
              type="button"
              variant="muted"
              onClick={() => setState({ editedTagName: '', editing: '' })}
              bg="white"
              color="#8F8F8F"
              justifyContent="center"
              alignItems="center"
              sx={{ borderRadius: '23px', border: '1px solid #8F8F8F' }}
              width={120}
              height={44}
            >
              Cancel
            </Button>

            <Button
              onClick={onDone}
              type="button" variant="primary"
              bg="#9B59B6"
              width={120}
              height={44}
              p={0}
              justifyContent="center"
              alignItems="center"
              sx={{ borderRadius: '23px', border: 0 }}
            >
              Save
            </Button>

          </Flex>
        </Box>
      )}
      {!editing && (
        <Button
          variant="transparent"
          onClick={() =>
            setState({
              confirmingDelete: { name: tag.name, id: tag.id },
            })
          }
          ml={2}
          p={0}
        >
          <FiTrash2 size={24} color="#64748B" />
        </Button>
      )}
    </Flex>
  </Card>
}

const SortableItem = sortableElement((props) => (
  <li
    style={{
      listStyleType: 'none',
    }}
  ><RenderChapter {...props} index={props.renderIndex} /></li>
));

const TagManagement = ({ onClickTag, onClose, chapters, revalidate, collection, storyId, journalId }) => {
  const [list, setList] = useState([])
  const { receiveDeletion } = useEntityActionCreators();

  const [{ editing, editedTagName, confirmingDelete }, setState] = useSpreadState({
    editing: '',
    editedTagName: '',
    confirmingDelete: '',
  });

  useEffect(() => {
    setList(chapters.sort((a, b) => a.position - b.position))
  }, [chapters])

  const handleTag = (fn, isFromPatch) =>
    fn()
      .then((res) => {
        if (!isFromPatch) {
          setList([...list, res.data.data?.attributes].sort((a, b) => a.position - b.position))
        }
        setState({ editing: '', editedTagName: '', confirmingDelete: '' })
        if (revalidate) revalidate();
      })

  if (confirmingDelete)
    return (
      <ConfirmDelete
        onDelete={() =>
          handleTag(async () => {
            await destroyChapter({ id: journalId, chapterId: confirmingDelete.id });
            receiveDeletion({ entityType: 'chapter', id: confirmingDelete.id });
          }, true)
        }
        onCancel={() => setState({ confirmingDelete: '' })}
        tag={confirmingDelete}
      />
    );

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const next = arrayMove(list, oldIndex, newIndex);
    const updatedList = next.map((ch, index) => ({ ...ch, position: index + 1 }))
    setList(updatedList)

    updatedList.map(((tag) => {
      patchChapters({
        id: journalId,
        chapterId: tag.id,
        attributes: { data: { attributes: { ...tag } } },
      })
    }))
  };

  return (
    <Box px={3}>
      <NewTag storyId={storyId} chapters={list} onSubmit={(value) => handleTag(() =>
        postChapters({
          id: journalId,
          attributes: { data: { attributes: { name: value } } },
        })
      )} />
      <Box mt={2} mb={7}>
        <Text
          sx={{
            color: '#000',
            fontFamily: 'Poppins',
            fontSize: '22px',
            fontWeight: '400',
            lineHeight: '165%',
            letterSpacing: '0.22px'
          }}
        >
          Edit and Move Chapters
        </Text>
        <SortableContainer helperClass='sortableHelper' useDragHandle onSortEnd={onSortEnd}>
          {list
            .map((tag, index) => {
              return (
                <SortableItem
                  key={`item-${tag.id}`}
                  index={index}
                  editing={editing}
                  tag={tag}
                  renderIndex={index + 1}
                  onClickTag={() => onClickTag(tag.name)}
                  setState={setState}
                  editedTagName={editedTagName}
                  onDone={() => handleTag(() =>
                    patchChapters({
                      id: journalId,
                      chapterId: tag.id,
                      attributes: { data: { attributes: { ...tag, name: editedTagName } } },
                    }),
                    true
                  )}
                />
              );
            })}
        </SortableContainer>
      </Box>
    </Box>
  );
};

TagManagement.propTypes = {
  collection: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  storyId: PropTypes.string,
  onClickTag: PropTypes.func,
};

TagManagement.defaultProps = {
  storyId: '',
  onClickTag: () => { },
};

export default TagManagement;
